import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { Company, Contact } from "@productlane/zero"
import { queries } from "@productlane/zero/queries"

const SEARCH_DEBOUNCE_MS = 200
const MAX_SELECTED = 100

function useDebounced(value: string) {
	const [debounced, setDebounced] = useState(value)
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), SEARCH_DEBOUNCE_MS)
		return () => clearTimeout(timer)
	}, [value])
	return debounced.trim()
}

/** Puts selected rows the capped list left out at the top, so a chosen filter stays visible. */
export function withSelectedFirst<T extends { id: string }>(
	options: readonly T[],
	selected: readonly T[],
	searching: boolean,
): T[] {
	if (searching) return [...options]
	const listed = new Set(options.map((row) => row.id))
	return [...selected.filter((row) => !listed.has(row.id)), ...options]
}

const idsOf = (selectedIds: readonly (string | null)[]) =>
	selectedIds
		.filter((id): id is string => typeof id === "string")
		.slice(0, MAX_SELECTED)

interface OptionsArgs {
	query?: string
	selectedIds?: readonly (string | null)[]
	enabled?: boolean
}

/** Companies for a picker or filter menu: searched on the server and capped, plus whatever is selected. */
export function useCompanyOptions({
	query = "",
	selectedIds = [],
	enabled = true,
}: OptionsArgs = {}) {
	const search = useDebounced(query)
	const ids = useMemo(() => idsOf(selectedIds), [selectedIds])
	const [options, result] = useQuery(
		queries.companyOptions({ query: search }),
		{ enabled },
	)
	const [selected] = useQuery(queries.companiesByIds(ids), {
		enabled: enabled && ids.length > 0,
	})
	const companies = useMemo<Company[]>(
		() => withSelectedFirst(options, selected, search !== ""),
		[options, selected, search],
	)
	return { companies, isComplete: result.type === "complete" }
}

/** Contacts for a picker or filter menu: searched on the server and capped, plus whatever is selected. */
export function useContactOptions({
	query = "",
	selectedIds = [],
	enabled = true,
}: OptionsArgs = {}) {
	const search = useDebounced(query)
	const ids = useMemo(() => idsOf(selectedIds), [selectedIds])
	const [options, result] = useQuery(
		queries.contactOptions({ query: search }),
		{ enabled },
	)
	const [selected] = useQuery(queries.contactsByIds(ids), {
		enabled: enabled && ids.length > 0,
	})
	const contacts = useMemo<Contact[]>(
		() => withSelectedFirst(options, selected, search !== ""),
		[options, selected, search],
	)
	return { contacts, isComplete: result.type === "complete" }
}
